/**
 * API Client with JWT authentication and interceptors
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { storage } from '../adapters/storage';
import { addDebugLog } from '../components/DebugPanel';
import { msg } from '../utils/messages';

// API Base URL - Using ZeroTier IP for mobile APK
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api';

// Debug logging function
const debugLog = (entry: any) => {
  addDebugLog(entry);
};

// Mobile detection
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 180000, // 3 minutes for large Excel uploads
});

// Log API client initialization
debugLog({
  level: 'info',
  category: 'network',
  message: 'API Client initialized',
  data: {
    baseUrl: API_BASE_URL,
    timeout: 180000,
    isMobile: isMobileDevice(),
    userAgent: navigator.userAgent
  }
});

// Request interceptor: Add JWT token and debug logging
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const startTime = Date.now();

    // Add metadata for timing
    (config as any).metadata = { startTime };

    // Get token from cross-platform storage adapter
    // This respects "Remember Me" setting (localStorage vs sessionStorage)
    let token: string | null = null;
    try {
      token = await storage.getItem('accessToken');
    } catch (error: any) {
      debugLog({
        level: 'warning',
        category: 'storage',
        message: 'Failed to get token from storage',
        data: { error: error.message }
      });
    }

    // Add token to headers if available
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request start
    debugLog({
      level: 'info',
      category: 'network',
      message: `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`,
      data: {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        hasAuth: !!token,
        timeout: config.timeout,
        headers: {
          ...config.headers,
          Authorization: config.headers.Authorization ? '[REDACTED]' : undefined
        },
        data: config.data ? '[REDACTED]' : undefined,
        isMobile: isMobileDevice()
      }
    });

    return config;
  },
  (error: AxiosError) => {
    debugLog({
      level: 'error',
      category: 'network',
      message: 'Request interceptor error',
      data: {
        error: error.message,
        code: error.code
      }
    });
    return Promise.reject(error);
  }
);

// Response interceptor: Handle token refresh and debug logging
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const duration = Date.now() - ((response.config as any).metadata?.startTime || Date.now());
    const responseSize = JSON.stringify(response.data).length;

    // Log successful response
    debugLog({
      level: 'success',
      category: 'network',
      message: `✅ API Response: ${response.config.url} (${duration}ms)`,
      data: {
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        status: response.status,
        statusText: response.statusText,
        duration: `${duration}ms`,
        responseSize: `${responseSize} bytes`,
        responseType: response.headers['content-type'],
        isMobile: isMobileDevice()
      }
    });

    return response;
  },
  async (error: AxiosError) => {
    const duration = Date.now() - ((error.config as any).metadata?.startTime || Date.now());
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Log error response
    debugLog({
      level: 'error',
      category: 'network',
      message: `❌ API Error: ${error.config?.url} (${duration}ms)`,
      data: {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
        statusText: error.response?.statusText,
        duration: `${duration}ms`,
        message: error.message,
        code: error.code,
        isNetworkError: !error.response,
        isTimeout: error.code === 'ECONNABORTED',
        isMobile: isMobileDevice(),
        retryCount: originalRequest._retry ? 1 : 0
      }
    });

    // Forbidden (403): show backend detail message as toast.
    // T9: NÃO emitir toast em 403 de LEITURA (GET/HEAD). Um usuário CLIENTE que
    // só visualiza a contagem dispara vários 403 de endpoints de leitura que ele
    // não pode ver (cirurgias, pós-contagem, download) — mostrar um toast vermelho
    // a cada um poluía a tela mesmo sem o usuário ter feito nenhuma ação. Mantém o
    // aviso apenas em ações de escrita (POST/PUT/PATCH/DELETE).
    if (error.response?.status === 403) {
      const method = (error.config?.method || 'get').toUpperCase();
      const isReadOnly = method === 'GET' || method === 'HEAD';
      if (!isReadOnly) {
        const detail = (error.response.data as any)?.detail;
        const message = typeof detail === 'string' && detail
          ? detail
          : 'Você não tem permissão para realizar esta ação.';
        msg.error(message);
      }
    }

    // Token expired (401) and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      debugLog({
        level: 'warning',
        category: 'auth',
        message: '🔄 Token expired, attempting refresh...',
        data: {
          url: error.config?.url,
          retryCount: 1
        }
      });

      try {
        // Get refresh token from cross-platform storage adapter
        const refreshToken = await storage.getItem('refreshToken');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Refresh token
        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh: refreshToken,
        });

        const { access } = refreshResponse.data;

        // Store new token using cross-platform storage adapter
        await storage.setItem('accessToken', access);

        debugLog({
          level: 'success',
          category: 'auth',
          message: '✅ Token refreshed successfully',
          data: {
            newTokenLength: access.length,
            retryUrl: error.config?.url
          }
        });

        // Retry original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError: any) {
        debugLog({
          level: 'error',
          category: 'auth',
          message: '❌ Token refresh failed',
          data: {
            error: refreshError.message,
            originalUrl: error.config?.url
          }
        });

        // Refresh failed → Logout (clear from all storages via adapter)
        await storage.removeItem('accessToken');
        await storage.removeItem('refreshToken');
        await storage.removeItem('user');

        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/mobile')) {
          debugLog({
            level: 'warning',
            category: 'auth',
            message: 'Redirecting to login due to auth failure'
          });
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
